import { FormFieldRenderer } from '@components/forms/FormFieldRenderer';
import { Input } from '@components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@components/ui/tabs';
import { ChevronsDownUp, ChevronsUpDown, Search, X } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { cn } from '@/utils';
import type { BoardField, BoardInstanceGroup, BoardSection } from '@/utils/humanCallModel';
import { groupBoardBlocks, multiCodeEntities } from '@/utils/humanCallModel';

import { BoardCodeRows, formatFieldValue } from './BoardCodeRows';
import { heardTimestamp, isHeardEligible } from './heard';
import { centerInScrollParent } from './scrollInPane';

interface FieldBoardProps {
  sections: BoardSection[];
  readOnly: boolean;
  /** True only while the call is live (CALL_STATUS_IN_PROGRESS). Gates mark-heard affordances. */
  callActive: boolean;
  heardAt: (key: string) => number | undefined;
  callStartMs?: number;
  onFieldChange: (key: string, value: unknown) => void;
  onFieldCommit: (key?: string) => void;
  toggleHeard: (key: string) => void;
  caseRecordId?: string;
  /** Validation messages keyed by field key (BV-2129). In the Call Script layout this
   *  pane replaced the accordion, which had always shown field errors — so without this
   *  the header could report "Validation errors found." while no field said which. */
  fieldErrors?: Record<string, string>;
  /**
   * Controlled All / Missed / Required narrowing (LA-7358). When supplied, this board
   * renders the CONTROL but the value lives with the caller, so the Call Script narrows
   * with it. Omit both to keep the board self-contained (its own state) — which is what
   * the unit tests do.
   */
  filter?: BoardFilter;
  onFilterChange?: (filter: BoardFilter) => void;
  /**
   * Fired on every row click, alongside the row's own edit / mark-heard behaviour —
   * including on read-only rows, which otherwise do nothing. The Call Script layout uses
   * it to scroll the script to the same field.
   */
  onRowSelect?: (key: string) => void;
  /** Node pinned before the "All fields" title — the Output sidebar's collapse control. */
  leadingActions?: React.ReactNode;
}

/**
 * Segmented filter over board rows. Not exported: the controlled props below are the
 * only external surface, and callers pass the identical `ScriptFilterMode` union —
 * an exported alias with no consumer hard-fails the knip gate.
 */
type BoardFilter = 'all' | 'missed' | 'required';

/** Imperative surface for the board (BV-2129). */
export interface FieldBoardHandle {
  /**
   * Bring one field into view, whatever the board's current state.
   *
   * A plain `querySelector('[data-field-id]')` is not enough here: the row may be
   * filtered out by the segmented filter, hidden by the search query, or inside a
   * collapsed section — in all three cases it is not in the DOM to find. So this clears
   * the narrowing first, expands the field's section, and only then scrolls.
   *
   * Scrolls the board's OWN container rather than calling scrollIntoView, for the reason
   * documented on Transcript's centerInTranscript: scrollIntoView moves every scrollable
   * ancestor, and this pane shares one with the Call Script column, so centring a field
   * here would shift the whole Review row.
   */
  revealField: (key: string) => void;
}

/** Status dot color classes using semantic dashboard tokens (no raw Tailwind colors). */
const DOT_CLS: Record<string, string> = {
  missing: 'bg-critical',
  heard: 'bg-attention',
  captured: 'bg-positive',
  prefilled: 'bg-muted-foreground/40',
  optional: 'border border-muted-foreground/40 bg-background',
};

/** A "filled" row carries a value (captured or prefilled). */
function isFilled(f: BoardField): boolean {
  return f.status === 'captured' || f.status === 'prefilled';
}

/** Format "heard · mm:ss" into call using the call start timestamp, or fall back. */
function fmtHeard(at: number | undefined, callStartMs?: number): string {
  const t = heardTimestamp(at, callStartMs);
  return t ? `heard · ${t}` : 'heard';
}

/** One entity instance (or the top-level fields) as nested sub-sections of ordinary rows. */
function BoardInstanceRows({
  group,
  renderRow,
}: {
  group: BoardInstanceGroup;
  renderRow: (f: BoardField) => React.ReactNode;
}) {
  return (
    <div className={cn(group.instance && 'mt-0.5')}>
      {/* Entity-instance sub-header (e.g. the drug/procedure code) */}
      {group.instance && (
        <div className="px-2 pb-0.5 pt-1.5 text-[11.5px] font-semibold text-foreground">
          {group.instance.label}
        </div>
      )}
      {group.sections.map((sub, si) => (
        <div
          key={sub.section ?? `s-${si}`}
          className={cn(group.instance && 'ml-2 border-l border-muted/60 pl-1.5')}
        >
          {/* Nested-object sub-header (e.g. "Coverage") */}
          {sub.section && (
            <div className="px-2 pt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              {sub.section}
            </div>
          )}
          {sub.fields.map(renderRow)}
        </div>
      ))}
    </div>
  );
}

/**
 * Right-pane "All fields" board for the Human Call tab.
 *
 * Shows ALL output leaf fields (via `buildFieldBoard`) grouped by schema section
 * with collapsible sticky headers. Each row has a status dot and a state label
 * (Missing / Optional / "heard · mm:ss") or — for FILLED fields — a compact
 * formatted value (enum code → human label). Click interaction when NOT readOnly:
 *   - empty + heard-eligible (text/number/date/…) → `toggleHeard(key)`
 *   - empty + enum (select/radio/…) → inline edit via FormFieldRenderer
 *   - filled → inline edit via FormFieldRenderer (setEditingKey)
 * Every row click — read-only included — also reports the key to `onRowSelect`.
 *
 * With two or more codes, an entity's fields collapse to one row per field with a chip
 * per code (`BoardCodeRows`, BV-2742); a chip click only navigates (no inline edit).
 *
 * Header carries a segmented All / Missed / Required filter (with counts) which
 * composes with the `/`-key search.
 *
 * Keyboard:
 *   - `/` focuses the search input (ignored while typing in an input/textarea)
 *   - `Enter` in the search marks the first visible empty heard-eligible field heard
 *   - `Esc` clears the search
 */
const FieldBoardComponent = React.forwardRef<FieldBoardHandle, FieldBoardProps>(function FieldBoard(
  {
    sections,
    fieldErrors,
    readOnly,
    callActive,
    heardAt,
    callStartMs,
    onFieldChange,
    onFieldCommit,
    toggleHeard,
    caseRecordId,
    filter: controlledFilter,
    onFilterChange,
    onRowSelect,
    leadingActions,
  },
  ref,
) {
  const [query, setQuery] = useState('');
  // Uncontrolled fallback, used only when the caller supplies neither prop.
  const [ownFilter, setOwnFilter] = useState<BoardFilter>('all');
  const filter = controlledFilter ?? ownFilter;
  const setFilter = useCallback(
    (next: BoardFilter) => (onFilterChange ? onFilterChange(next) : setOwnFilter(next)),
    [onFilterChange],
  );
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [editingKey, setEditingKey] = useState<string | null>(null);
  // The multi-code chip last clicked or revealed — its row is highlighted (BV-2742).
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const allFields = useMemo(() => sections.flatMap(s => s.fields), [sections]);

  const counts = useMemo(
    () => ({
      missing: allFields.filter(f => f.status === 'missing').length,
      required: allFields.filter(f => f.required).length,
    }),
    [allFields],
  );

  const q = query.trim().toLowerCase();
  const matchesSearch = (f: BoardField) =>
    !q || f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q);
  const matchesFilter = (f: BoardField) =>
    filter === 'all' ||
    (filter === 'missed' && f.status === 'missing') ||
    (filter === 'required' && f.required);
  const matches = (f: BoardField) => matchesSearch(f) && matchesFilter(f);

  // Two steps, because the row is usually NOT in the DOM when revealField is called: the
  // filter, the search or a collapsed section may be hiding it, and the state updates that
  // undo those have not committed yet. Scrolling from inside the handle (or from a
  // requestAnimationFrame scheduled there) therefore queried a row that did not exist yet
  // and silently did nothing. So the handle only records WHAT to reveal, and an effect —
  // which React runs after the DOM is updated — does the scrolling.
  const [pendingReveal, setPendingReveal] = useState<string | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      revealField: (key: string) => {
        const section = sections.find(sec => sec.fields.some(f => f.key === key));
        // Undo whatever is hiding the row.
        setFilter('all');
        setQuery('');
        if (section) {
          setCollapsed(prev => {
            if (!prev.has(section.title)) return prev;
            const next = new Set(prev);
            next.delete(section.title);
            return next;
          });
        }
        setPendingReveal(key);
        setSelectedKey(key);
      },
    }),
    [sections, setFilter],
  );

  // Runs after the un-hiding above has rendered. Re-runs as filter/query/collapsed settle,
  // so a row that needed two commits to appear is still caught.
  useEffect(() => {
    if (!pendingReveal) return;
    const list = scrollRef.current;
    // Matched on the dataset rather than an attribute selector: field keys are dot/bracket
    // paths (`drug.0.fax_number`), and building a selector from one needs CSS.escape,
    // which jsdom does not implement. Comparing the parsed value sidesteps escaping.
    const el = list
      ? Array.from(list.querySelectorAll<HTMLElement>('[data-field-id]')).find(
          node => node.dataset.fieldId === pendingReveal,
        )
      : undefined;
    if (!el) return;

    // Whichever element is really doing the scrolling — not assumed to be our own list:
    // the list div is unbounded (the root has no height constraint), so an ancestor scrolls.
    centerInScrollParent(el);
    setPendingReveal(null);
  }, [pendingReveal, filter, query, collapsed, sections]);

  // Global `/` shortcut: focus search when not already typing in an input/textarea.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement | null)?.tagName;
      if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Clear editingKey if the field it points to is no longer present in allFields
  // (e.g. a disposition recompute removed the section containing that field).
  useEffect(() => {
    if (editingKey !== null && !allFields.some(f => f.key === editingKey)) {
      setEditingKey(null);
    }
  }, [allFields, editingKey]);

  /** Handle keystrokes inside the search input. */
  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setQuery('');
      searchRef.current?.blur();
      return;
    }
    if (e.key === 'Enter' && callActive) {
      // Mark the first visible empty heard-eligible field as heard.
      const first = allFields.find(
        f =>
          matches(f) &&
          (f.status === 'missing' || f.status === 'optional') &&
          isHeardEligible(f.type),
      );
      if (first) toggleHeard(first.key);
    }
  };

  /** Handle a click on an EMPTY board row (non-editing state). */
  const onEmptyRowClick = (f: BoardField) => {
    onRowSelect?.(f.key);
    if (readOnly) return;
    // Mark-heard is a LIVE-call affordance only. Off the call (still within the edit
    // window) an empty heard-eligible field is filled inline instead.
    if (callActive && isHeardEligible(f.type)) {
      toggleHeard(f.key);
      return;
    }
    setEditingKey(f.key);
  };

  /** Handle a click on a multi-code chip: navigation only — select it and scroll the script. */
  const onCodeCellClick = (key: string) => {
    onRowSelect?.(key);
    setSelectedKey(key);
  };

  /** Toggle collapse state for a section (suppressed while query is active). */
  const toggleCollapse = (title: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  // Panel-level collapse-all / expand-all over every section. (Collapse state is
  // ignored visually while a search query is active, same as per-section toggles.)
  const allCollapsed = sections.length > 0 && sections.every(s => collapsed.has(s.title));
  const toggleAllSections = () =>
    setCollapsed(allCollapsed ? new Set() : new Set(sections.map(s => s.title)));

  /** One board row in its current mode: filled → compact value; editing → widget; empty → status. */
  /**
   * A row plus its validation message, when it has one (BV-2129).
   *
   * The message cannot live inside the row variants: only the inline-EDIT branch renders
   * a FormFieldRenderer, and that is the one component here that knows how to draw an
   * `error`. A filled row shows a formatted value and an empty row shows a status label —
   * neither has a widget — so a field flagged on Save displayed nothing at all once the
   * reveal scrolled to it. Wrapping is what makes the message independent of which
   * variant the row happens to be in.
   */
  const renderRow = (f: BoardField) => {
    const error = fieldErrors?.[f.key];
    const row = renderRowBody(f);
    if (!error) return row;
    return (
      <div key={f.key} className="border-l-2 border-destructive">
        {row}
        <p className="px-2 pb-1 text-[11px] text-destructive">{error}</p>
      </div>
    );
  };

  const renderRowBody = (f: BoardField) => {
    // ── Filled rows: compact dot + formatted value; click opens inline edit ──
    // Note: editingKey check below handles the inline-edit case for both
    // filled and empty-enum rows, so we only render the compact view when
    // this field is NOT currently being edited.
    if (isFilled(f) && editingKey !== f.key) {
      return (
        <div
          key={f.key}
          data-field-id={f.key}
          className={cn(
            'group flex items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px]',
            (!readOnly || onRowSelect) && 'cursor-pointer hover:bg-muted/40',
          )}
          title={readOnly ? undefined : 'Click to edit'}
          onClick={() => {
            onRowSelect?.(f.key);
            if (!readOnly) setEditingKey(f.key);
          }}
        >
          <span className="flex-1 break-words text-muted-foreground" title={f.label}>
            {f.label}
          </span>
          <span className="flex shrink-0 items-center gap-1.5">
            <span
              className={cn('h-1.5 w-1.5 rounded-full', DOT_CLS[f.status] ?? DOT_CLS.optional)}
            />
            <span
              className={cn(
                'max-w-[160px] truncate text-[12.5px]',
                f.status === 'captured' ? 'font-medium text-foreground' : 'text-muted-foreground',
              )}
              title={formatFieldValue(f.value, f.field)}
            >
              {formatFieldValue(f.value, f.field)}
            </span>
          </span>
        </div>
      );
    }

    // ── Empty enum inline editing mode (click-to-edit via FormFieldRenderer) ──
    if (editingKey === f.key) {
      return (
        <div
          key={f.key}
          data-field-id={f.key}
          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px]"
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
              (e.target as HTMLInputElement).blur();
            }
            if (e.key === 'Escape') setEditingKey(null);
          }}
        >
          <span className="flex-1 break-words text-muted-foreground" title={f.label}>
            {f.label}
          </span>
          <div className="w-[150px]">
            <FormFieldRenderer
              field={{ ...f.field, disabled: readOnly }}
              value={f.value}
              error={fieldErrors?.[f.key]}
              onChange={v => onFieldChange(f.key, v)}
              onBlur={() => {
                // Keyed commit: re-validates this field as focus leaves, so the
                // message survives the row dropping back out of edit mode (LA-7358).
                onFieldCommit(f.key);
                setEditingKey(null);
              }}
              caseRecordId={caseRecordId}
              hideLabel
              compact
            />
          </div>
        </div>
      );
    }

    // ── Empty / heard display mode ──
    const clickTitle = readOnly
      ? undefined
      : callActive && isHeardEligible(f.type)
        ? 'Click to mark heard'
        : 'Click to edit';

    return (
      <div
        key={f.key}
        data-field-id={f.key}
        className={cn(
          'group flex items-center gap-2 rounded-md border-l-2 border-transparent px-2 py-1.5 text-[12.5px]',
          (!readOnly || onRowSelect) && 'cursor-pointer hover:bg-muted/40',
        )}
        title={clickTitle}
        onClick={() => onEmptyRowClick(f)}
      >
        <span className="flex-1 break-words text-muted-foreground" title={f.label}>
          {f.label}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          <span className={cn('h-1.5 w-1.5 rounded-full', DOT_CLS[f.status] ?? DOT_CLS.optional)} />
          {f.status === 'missing' && (
            <span className="text-[11px] font-semibold text-critical-foreground">
              {f.required ? 'Missing' : 'Optional'}
            </span>
          )}
          {f.status === 'optional' && (
            <span className="text-[11px] font-medium text-muted-foreground">Optional</span>
          )}
          {f.status === 'heard' && (
            <span className="rounded-md bg-attention/40 px-1.5 text-[11px] font-semibold text-attention-foreground">
              {fmtHeard(heardAt(f.key), callStartMs)}
            </span>
          )}
        </span>
      </div>
    );
  };

  return (
    // `min-h-0 flex-1` is what makes the list below scroll instead of the whole pane
    // (BV-2129): without a bounded height the child's `flex-1 overflow-y-auto` never
    // becomes a scroller, so an ancestor scrolled everything — header, filter and search
    // bar included — and nothing could stay frozen.
    <div className="flex min-h-0 flex-1 flex-col">
      {/* ── Header: title + segmented filter + search — frozen; the list scrolls ── */}
      <div className="shrink-0 border-b border-border px-4 pb-3 pt-4">
        <div className="mb-2.5 flex items-center justify-between gap-3">
          <div className="flex shrink-0 items-center gap-2">
            {leadingActions}
            <h2 className="whitespace-nowrap text-sm font-semibold text-foreground">All fields</h2>
          </div>
          <div className="flex items-center gap-1.5">
            <Tabs value={filter} onValueChange={value => setFilter(value as BoardFilter)}>
              <TabsList className="h-7">
                <TabsTrigger
                  value="all"
                  className="h-6 px-2 text-xs text-muted-foreground data-[state=active]:text-foreground"
                >
                  All
                </TabsTrigger>
                <TabsTrigger
                  value="missed"
                  className="h-6 px-2 text-xs text-muted-foreground data-[state=active]:text-foreground"
                  aria-label={`Missed (${counts.missing})`}
                >
                  Missed
                  <span
                    className={cn(
                      'ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded px-1 text-[10px] font-semibold',
                      counts.missing === 0
                        ? 'bg-positive/40 text-positive-foreground'
                        : 'bg-critical/30 text-critical-foreground',
                    )}
                  >
                    {counts.missing}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="required"
                  className="h-6 px-2 text-xs text-muted-foreground data-[state=active]:text-foreground"
                  aria-label={`Required (${counts.required})`}
                >
                  Required
                  <span className="ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded bg-muted px-1 text-[10px] font-semibold text-muted-foreground">
                    {counts.required}
                  </span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
            {sections.length > 0 && (
              <button
                type="button"
                onClick={toggleAllSections}
                title={allCollapsed ? 'Expand all sections' : 'Collapse all sections'}
                aria-label={allCollapsed ? 'Expand all sections' : 'Collapse all sections'}
                className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              >
                {allCollapsed ? (
                  <ChevronsUpDown className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <ChevronsDownUp className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Search input. The clear button only exists while there is something to clear,
            so the empty state keeps its original look; `pr-8` is applied only then, for the
            same reason. Esc still clears AND blurs (see onSearchKey) — the button keeps
            focus in the field instead, since clicking it means "search for something else". */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onSearchKey}
            placeholder="Find a field…  ( / )"
            className={cn('h-8 pl-8 text-[12.5px]', query && 'pr-8')}
            aria-label="Search fields"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                searchRef.current?.focus();
              }}
              title="Clear search"
              aria-label="Clear search"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* ── Scrollable section list ── */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto pb-4">
        {sections.map(sec => {
          const rows = sec.fields.filter(matches);
          if (rows.length === 0) return null;

          // Collapse state is ignored while a search query is active.
          const isCollapsed = collapsed.has(sec.title) && !q;
          const nMissing = sec.fields.filter(f => f.status === 'missing').length;
          const nHeard = sec.fields.filter(f => f.status === 'heard').length;

          return (
            <div key={sec.title} className="border-b border-muted/60">
              {/* Sticky section header */}
              <div
                className="sticky top-0 z-[5] flex cursor-pointer select-none items-center gap-2 bg-background px-3.5 pb-2 pt-3 hover:bg-muted/40"
                onClick={() => toggleCollapse(sec.title)}
              >
                <span
                  className={cn(
                    'text-[9px] text-muted-foreground transition-transform',
                    isCollapsed && '-rotate-90',
                  )}
                >
                  ▼
                </span>
                <span className="flex-1 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
                  {sec.title}
                </span>
                {/* Per-section mini counts */}
                {nMissing > 0 && (
                  <span className="rounded-full bg-critical/30 px-1.5 text-[10px] font-semibold text-critical-foreground">
                    {nMissing}
                  </span>
                )}
                {nHeard > 0 && (
                  <span className="rounded-full bg-attention/40 px-1.5 text-[10px] font-semibold text-attention-foreground">
                    {nHeard}
                  </span>
                )}
                {nMissing === 0 && (
                  <span className="text-[11px] font-semibold text-positive-foreground">✓</span>
                )}
              </div>

              {/* Section rows (hidden when collapsed) — nested by entity instance → sub-section */}
              {!isCollapsed && (
                <div className="px-2 pb-2.5">
                  {groupBoardBlocks(rows, multiCodeEntities(sec.fields)).map((block, gi) =>
                    block.kind === 'codes' ? (
                      <BoardCodeRows
                        key={`codes-${block.entity}-${gi}`}
                        sections={block.sections}
                        selectedKey={selectedKey}
                        fieldErrors={fieldErrors}
                        onCellClick={onCodeCellClick}
                      />
                    ) : (
                      <BoardInstanceRows
                        key={block.group.instance?.key ?? `top-${gi}`}
                        group={block.group}
                        renderRow={renderRow}
                      />
                    ),
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

// Memoized so non-keystroke re-renders (e.g. `isSaving` toggles, background
// refetches) are skipped when props are shallow-equal. The `sections` (board)
// prop still changes per keystroke by necessity, so those re-renders are preserved.
export const FieldBoard = React.memo(FieldBoardComponent);
FieldBoard.displayName = 'FieldBoard';
